import { lazy, Suspense, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import type { DashboardSpec, Section } from "./types";
import { SectionShell } from "./SectionShell";
import { SectionErrorBoundary } from "./SectionErrorBoundary";
import { SectionSkeleton } from "./SectionSkeleton";
import { SaveButton } from "./SaveButton";
import { isLikeable } from "./useSavedComponents";
import { AnswerSection } from "./sections/AnswerSection";
import { KpiRow } from "./sections/KpiRow";
import { SectionTable } from "./sections/SectionTable";
import { SectionList } from "./sections/SectionList";
import { SectionMarkdown } from "./sections/SectionMarkdown";
import { SectionTabGroup } from "./sections/SectionTabGroup";
import { SectionCallout } from "./sections/SectionCallout";
import { SectionBullet } from "./sections/SectionBullet";
import { SectionBarList } from "./sections/SectionBarList";
import { SectionTracker } from "./sections/SectionTracker";
import { TierBar } from "./sections/TierBar";
import { StoreProgress } from "./sections/StoreProgress";
import { FilterTable } from "./sections/FilterTable";
import { FlatPanelNotes } from "./NotesAffordance";

// Recharts-heavy sections are lazy-loaded so the concise-first default view
// (answer + kpi_row + followups) ships zero chart code. Per R8 lock.
const LazySectionChart = lazy(() =>
  import("./sections/SectionChart").then((m) => ({ default: m.SectionChart })),
);
const LazySectionStatTable = lazy(() =>
  import("./sections/SectionStatTable").then((m) => ({ default: m.SectionStatTable })),
);
const LazySectionSparkline = lazy(() =>
  import("./sections/SectionSparkline").then((m) => ({ default: m.SectionSparkline })),
);

// Module-level warm flags: once a chunk has been prefetched (or eagerly loaded)
// in this session, never schedule it again — import() is cached, but this also
// stops re-registering idle callbacks across every renderer mount.
const chunkWarmed: Record<"chart" | "stat_table" | "sparkline", boolean> = {
  chart: false,
  stat_table: false,
  sparkline: false,
};
const warmSectionChunk = (kind: "chart" | "stat_table" | "sparkline") => {
  if (chunkWarmed[kind]) return;
  chunkWarmed[kind] = true;
  if (kind === "chart") void import("./sections/SectionChart");
  else if (kind === "stat_table") void import("./sections/SectionStatTable");
  else void import("./sections/SectionSparkline");
};

interface DashboardRendererProps {
  spec: DashboardSpec | null;
  streaming?: boolean;
  /** When true, all sections render with no animation — used for PDF capture. */
  staticMode?: boolean;
  /** Filter applied AFTER the answer/kpi pinning; used by concise-first to hide the rest. */
  hiddenKinds?: ReadonlySet<Section["kind"]>;
  /** Show per-component Save buttons on likeable sections (default true). The
   *  My Dashboard board renders saved sections with this off. */
  showSaveButtons?: boolean;
}

/**
 * Top-level renderer for a (possibly partial) DashboardSpec. Walks
 * spec.sections[] and dispatches by kind. Answer sections render as the lead
 * (no shell, no collapse). While streaming, a trailing skeleton card holds
 * layout for the next section.
 *
 * Reveal: 200-300 ms ease cross-fade per R3 (auto-respects prefers-reduced-
 * motion via Framer Motion's `Reduce Motion`).
 */
export function DashboardRenderer({
  spec,
  streaming,
  staticMode,
  hiddenKinds,
  showSaveButtons = true,
}: DashboardRendererProps) {
  const reduce = useReducedMotion();

  // Only the lazy kinds the current spec actually contains are worth warming.
  // Stable string key so the effect re-runs when the present-kinds set changes,
  // not on every streaming frame's new spec object identity.
  const lazyKindsKey = Array.isArray(spec?.sections)
    ? [...new Set(spec.sections.map((s) => s.kind))]
        .filter((k): k is "chart" | "stat_table" | "sparkline" =>
          k === "chart" || k === "stat_table" || k === "sparkline",
        )
        .sort()
        .join(",")
    : "";

  // Warm the code-split section chunks during idle time so the first "See more"
  // expand or table row-detail open doesn't stall on a chunk fetch (2026-05-31:
  // "table loads slow on press"). Gated to the kinds the spec references, and
  // skipped in staticMode (PDF / collapse clone) since the live render warmed them.
  useEffect(() => {
    if (staticMode || !lazyKindsKey) return;
    const kinds = lazyKindsKey.split(",") as Array<"chart" | "stat_table" | "sparkline">;
    const warm = () => kinds.forEach(warmSectionChunk);
    if (typeof requestIdleCallback === "function") {
      const id = requestIdleCallback(warm);
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 1200);
    return () => clearTimeout(id);
  }, [staticMode, lazyKindsKey]);

  if (!spec) return null;

  // Defensive: never deref spec.sections directly — a malformed spec can land
  // here with sections undefined and crash the render. DashboardCard guards
  // too; this keeps the PDF/static path (which renders this directly) safe.
  const all = Array.isArray(spec.sections) ? spec.sections : [];
  const visibleSections = hiddenKinds
    ? all.filter((s) => !hiddenKinds.has(s.kind))
    : all;

  if (staticMode) {
    return (
      <section className="space-y-3">
        {visibleSections.map((section, i) => (
          // data-pdf-block marks a whole-section page-break boundary so the PDF
          // exporter (dashboardPdfExport) can snap page cuts to section edges
          // instead of shearing a card across the page break.
          <div data-pdf-block key={i}>
            <SectionErrorBoundary label={section.kind}>
              <SectionDispatch section={section} question={spec.question} showSaveButtons={showSaveButtons} />
            </SectionErrorBoundary>
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <motion.div
        className="space-y-3"
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.05 } } }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {visibleSections.map((section, i) => (
            <motion.div
              key={`${section.kind}-${i}`}
              layout
              variants={{
                hidden: { opacity: 0, y: 8 },
                show: { opacity: 1, y: 0 },
              }}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
            >
              <SectionErrorBoundary label={section.kind}>
                <SectionDispatch section={section} question={spec.question} showSaveButtons={showSaveButtons} />
              </SectionErrorBoundary>
            </motion.div>
          ))}
          {streaming ? (
            // Lightweight "writing" indicator while more sections stream in —
            // replaces the full empty SectionSkeleton card so only content that
            // has actually arrived is shown (2026-05-31 review). The
            // StatusBanner carries the announced progress, so this is aria-hidden.
            <motion.div
              key="__streaming__"
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="flex items-center gap-1.5 px-1 py-2"
              aria-hidden
            >
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/50"
                  animate={reduce ? { opacity: 0.6 } : { opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                  transition={
                    reduce
                      ? { duration: 0 }
                      : { duration: 0.9, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }
                  }
                />
              ))}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}

function SectionDispatch({
  section,
  question,
  showSaveButtons = true,
}: {
  section: Section;
  question?: string;
  showSaveButtons?: boolean;
}) {
  // Save-to-My-Dashboard heart, rendered in the header of likeable (visual /
  // data) components only. Null for textual / KPI sections, and in contexts
  // that opt out (the My Dashboard board itself).
  const saveAccessory =
    showSaveButtons && isLikeable(section) ? (
      <SaveButton section={section} panelQuestion={question ?? ""} />
    ) : null;

  switch (section.kind) {
    case "answer":
      return <AnswerSection title={section.title} body={section.body} />;
    case "kpi_row":
      return (
        <SectionShell completeness={section.completeness}>
          {section.notes ? (
            // kpi_row is shell-less (no title bar), so the notes pill gets the
            // same minimal title-row anchor flat contexts use.
            <FlatPanelNotes notes={section.notes}>
              <KpiRow items={section.items} />
            </FlatPanelNotes>
          ) : (
            <KpiRow items={section.items} />
          )}
        </SectionShell>
      );
    case "table":
      return (
        <SectionShell
          title={section.title}
          meta={`${section.rows.length} rows`}
          completeness={section.completeness}
          notes={section.notes}
          headerAccessory={saveAccessory}
        >
          <SectionTable
            title={section.title}
            columns={section.columns}
            rows={section.rows}
            rowDetail={section.row_detail}
            evidencePerRow={section.evidence_per_row}
            storeNumberPerRow={section.store_number_per_row}
            head={section.head}
            noun={section.noun}
            emptyState={section.empty_state}
          />
        </SectionShell>
      );
    case "chart":
      return (
        <SectionShell
          title={section.title}
          completeness={section.completeness}
          notes={section.notes}
          headerAccessory={saveAccessory}
        >
          <Suspense fallback={<SectionSkeleton shape="chart" />}>
            <LazySectionChart
              title={section.title}
              chartType={section.chart_type}
              data={section.data}
              xKey={section.x_key}
              series={section.series}
            />
          </Suspense>
        </SectionShell>
      );
    case "list":
      return (
        <SectionShell
          title={section.title}
          meta={`${section.items.length}`}
          completeness={section.completeness}
          notes={section.notes}
        >
          <SectionList title={section.title} items={section.items} />
        </SectionShell>
      );
    case "markdown":
      return (
        <SectionShell title={section.title ?? "Notes"}>
          <SectionMarkdown body={section.body} />
        </SectionShell>
      );
    case "tab_group":
      return (
        <SectionShell>
          <SectionTabGroup tabs={section.tabs} />
        </SectionShell>
      );
    case "callout":
      return (
        <SectionCallout
          severity={section.severity}
          body={section.body}
          title={section.title}
        />
      );
    case "stat_table":
      return (
        <SectionShell
          title={section.title}
          meta={`${section.rows.length} rows`}
          completeness={section.completeness}
          headerAccessory={saveAccessory}
        >
          <Suspense fallback={<SectionSkeleton shape="table" />}>
            <LazySectionStatTable
              title={section.title}
              columns={section.columns}
              rows={section.rows}
              evidencePerRow={section.evidence_per_row}
            />
          </Suspense>
        </SectionShell>
      );
    case "bullet":
      return (
        <SectionShell title={section.title} notes={section.notes} headerAccessory={saveAccessory}>
          <SectionBullet
            title={section.title}
            value={section.value}
            target={section.target}
            bands={section.bands}
            unit={section.unit}
          />
        </SectionShell>
      );
    case "sparkline":
      return (
        <SectionShell title={section.title} headerAccessory={saveAccessory}>
          <Suspense fallback={<SectionSkeleton shape="chart" />}>
            <LazySectionSparkline
              title={section.title}
              data={section.data}
              xKey={section.x_key}
              yKey={section.y_key}
            />
          </Suspense>
        </SectionShell>
      );
    case "bar_list":
      return (
        <SectionShell
          title={section.title}
          meta={`${section.items.length}`}
          completeness={section.completeness}
          notes={section.notes}
          headerAccessory={saveAccessory}
        >
          <SectionBarList
            title={section.title}
            items={section.items}
            head={section.head}
            noun={section.noun}
            emptyState={section.empty_state}
          />
        </SectionShell>
      );
    case "tracker":
      return (
        <SectionShell
          title={section.title}
          meta={`${section.cells.length}`}
          notes={section.notes}
          headerAccessory={saveAccessory}
        >
          <SectionTracker title={section.title} cells={section.cells} />
        </SectionShell>
      );
    case "tier_bar":
      return (
        <SectionShell
          title={section.title}
          completeness={section.completeness}
          notes={section.notes}
          headerAccessory={saveAccessory}
        >
          <TierBar
            segments={section.segments}
            value={section.value}
            valueLabel={section.value_label}
            target={section.target}
            unit={section.unit}
          />
        </SectionShell>
      );
    case "store_progress":
      return (
        <SectionShell
          title={section.title}
          meta={`${section.items.length}`}
          completeness={section.completeness}
          notes={section.notes}
          headerAccessory={saveAccessory}
        >
          <StoreProgress items={section.items} target={section.target} unit={section.unit} />
        </SectionShell>
      );
    case "filter_table":
      return (
        <SectionShell
          title={section.title}
          meta={`${section.rows.length} rows`}
          completeness={section.completeness}
          notes={section.notes}
          headerAccessory={saveAccessory}
        >
          <FilterTable
            title={section.title}
            columns={section.columns}
            rows={section.rows}
            flags={section.flags}
            scope={section.scope}
            head={section.head}
            noun={section.noun}
            defaultSort={section.default_sort}
            emptyState={section.empty_state}
          />
        </SectionShell>
      );
    default:
      return null;
  }
}
